// One AWS Transcribe streaming session over its websocket API (TT-1102).
//
// The URL is presigned by appback (`…/live-transcript/stt-session`), so the
// browser holds no AWS credentials — it just speaks event-stream on the
// socket: AudioEvents out, TranscriptEvents in. Speaker labels ride the
// Items; `speakerRuns.js` turns them into pieces.

import { decodeTranscribeMessage, encodeAudioEvent } from "./eventStream";

const EMPTY = new Uint8Array(0);

export function openTranscribeSession({ wsUrl, onOpen, onResult, onError, onClose }) {
  const ws = new WebSocket(wsUrl);
  ws.binaryType = "arraybuffer";
  let ended = false;

  ws.onopen = () => onOpen?.();

  ws.onmessage = (e) => {
    let msg;
    try {
      msg = decodeTranscribeMessage(e.data);
    } catch (err) {
      onError?.(err);
      return;
    }
    if (msg.type === "exception") {
      onError?.(new Error(`${msg.event || "TranscribeException"}: ${msg.payload?.Message || ""}`));
      return;
    }
    if (msg.event !== "TranscriptEvent") return;
    const results = msg.payload?.Transcript?.Results || [];
    results.forEach((result) => onResult?.(result));
  };

  ws.onerror = () => onError?.(new Error("Transcribe socket error"));
  ws.onclose = (e) => onClose?.({ code: e.code, reason: e.reason, ended });

  return {
    // Ship one PCM frame; frames sent before the socket is open are dropped
    // (the caller only starts pumping after onOpen).
    send(pcmBytes) {
      if (ws.readyState !== WebSocket.OPEN) return false;
      ws.send(encodeAudioEvent(pcmBytes));
      return true;
    },
    // An empty AudioEvent is the end-of-stream marker; Transcribe flushes any
    // pending FINAL and closes from its side.
    end() {
      if (ended) return;
      ended = true;
      if (ws.readyState === WebSocket.OPEN) ws.send(encodeAudioEvent(EMPTY));
    },
    close() {
      ended = true;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
    },
    get open() {
      return ws.readyState === WebSocket.OPEN;
    },
  };
}
